import { PathSet, Position, Snapshot } from '../types';
import { TransitionMode } from '../store/app';
import {
  PieceKind,
  anchorPath,
  getCumulativeLengths,
  getPathSetEntry,
  reversePath,
  getPositionAlongPath,
} from './path';

// Transition configuration constants
export const FPS = 30;
export const TRANSITION_TIME = 1200; // milliseconds (only for TIME_BASED mode)
export const TRANSITION_SPEED = 8; // pixels per second (only for DISTANCE_BASED mode)

// Helper: Calculate Euclidean distance between two positions
export const getDistance = (p1: Position, p2: Position): number => {
  const dx = (p1.x as number) - (p2.x as number);
  const dy = (p1.y as number) - (p2.y as number);
  return Math.sqrt(dx * dx + dy * dy);
};

// A recorded path resolved for one transition: anchored to the actual
// endpoint positions, direction applied, cumulative arc lengths precomputed
// so the 30 FPS ticks sample in O(log n).
export type PreparedPath = {
  points: Position[];
  cumulative: number[];
  length: number;
};

// Playback-ready paths for one snapshot transition; index-aligned with the
// Snapshot arrays, null for pieces that move linearly.
export type TransitionPaths = {
  players: (PreparedPath | null)[];
  opponents: (PreparedPath | null)[];
  goalkeepers: (PreparedPath | null)[];
  ball: PreparedPath | null;
};

const preparePiecePath = (
  pathSet: PathSet | null | undefined,
  kind: PieceKind,
  index: number,
  startPos: Position | undefined,
  endPos: Position | undefined,
  reversed: boolean,
): PreparedPath | null => {
  const entry = getPathSetEntry(pathSet, kind, index);
  if (!entry || entry.points.length < 2 || !startPos || !endPos) {
    return null;
  }
  let points = reversed ? reversePath(entry.points) : entry.points;
  // Snapshot edits (re-drag, frame remove/reorder) can desynchronize stored
  // path endpoints from the snapshot positions; anchoring degrades that into
  // linear lead-in/out segments instead of a teleport.
  points = anchorPath(points, startPos as Position, endPos as Position);
  const cumulative = getCumulativeLengths(points);
  return {
    points,
    cumulative,
    length: cumulative[cumulative.length - 1],
  };
};

// Resolve the recorded paths of one transition for playback. pathSet is the
// stored PathSet describing this transition: snapshots[to].paths when
// playing forward, snapshots[from].paths (with reversed=true) when stepping
// backward. Returns null when no piece has a path — callers fall back to the
// plain linear behaviour with zero overhead.
export const prepareTransitionPaths = (
  startSnapshot: Snapshot,
  endSnapshot: Snapshot,
  pathSet: PathSet | null | undefined,
  reversed: boolean,
): TransitionPaths | null => {
  if (!pathSet) {
    return null;
  }
  const prepared: TransitionPaths = {
    ball: preparePiecePath(
      pathSet,
      'ball',
      0,
      startSnapshot.ball,
      endSnapshot.ball,
      reversed,
    ),
    players: startSnapshot.players.map((startPos, i) =>
      preparePiecePath(
        pathSet,
        'players',
        i,
        startPos as Position,
        endSnapshot.players[i] as Position,
        reversed,
      ),
    ),
    opponents: startSnapshot.opponents.map((startPos, i) =>
      preparePiecePath(
        pathSet,
        'opponents',
        i,
        startPos as Position,
        endSnapshot.opponents[i] as Position,
        reversed,
      ),
    ),
    goalkeepers: startSnapshot.goalkeepers.map((startPos, i) =>
      preparePiecePath(
        pathSet,
        'goalkeepers',
        i,
        startPos as Position,
        endSnapshot.goalkeepers[i] as Position,
        reversed,
      ),
    ),
  };
  const hasAny =
    prepared.ball ||
    prepared.players.some(Boolean) ||
    prepared.opponents.some(Boolean) ||
    prepared.goalkeepers.some(Boolean);
  return hasAny ? prepared : null;
};

// Distance a piece actually travels in a transition: its path arc length
// when it follows a recorded path, the straight-line distance otherwise.
// DISTANCE_BASED durations must use this or pieces would visibly speed up
// along curves.
const getEffectiveDistance = (
  startPos: Position,
  endPos: Position,
  prepared: PreparedPath | null | undefined,
): number =>
  prepared ? prepared.length : getDistance(startPos, endPos);

// Helper: Calculate maximum distance moved in a snapshot transition
export const getMaxDistanceInTransition = (
  startSnapshot: Snapshot,
  endSnapshot: Snapshot,
  transitionPaths?: TransitionPaths | null,
): number => {
  let maxDistance = 0;

  // Check ball distance
  maxDistance = Math.max(
    maxDistance,
    getEffectiveDistance(
      startSnapshot.ball,
      endSnapshot.ball,
      transitionPaths?.ball,
    ),
  );

  // Check all players
  startSnapshot.players.forEach((startPos, i) => {
    const endPos = endSnapshot.players[i];
    if (startPos && endPos) {
      maxDistance = Math.max(
        maxDistance,
        getEffectiveDistance(
          startPos as Position,
          endPos as Position,
          transitionPaths?.players[i],
        ),
      );
    }
  });

  // Check all goalkeepers
  startSnapshot.goalkeepers.forEach((startPos, i) => {
    const endPos = endSnapshot.goalkeepers[i];
    if (startPos && endPos) {
      maxDistance = Math.max(
        maxDistance,
        getEffectiveDistance(
          startPos as Position,
          endPos as Position,
          transitionPaths?.goalkeepers[i],
        ),
      );
    }
  });

  // Check all opponents
  startSnapshot.opponents.forEach((startPos, i) => {
    const endPos = endSnapshot.opponents[i];
    if (startPos && endPos) {
      maxDistance = Math.max(
        maxDistance,
        getEffectiveDistance(
          startPos as Position,
          endPos as Position,
          transitionPaths?.opponents[i],
        ),
      );
    }
  });

  return maxDistance;
};

// Helper: Calculate transition frame count for an individual object based on distance
export const calculateFrameCountForDistance = (distance: number): number => {
  const transitionTimeSeconds = distance / TRANSITION_SPEED;
  return Math.max(1, Math.round(transitionTimeSeconds * FPS));
};

// Helper: Calculate transition frame count based on mode
export const calculateTransitionFrameCount = (
  startSnapshot: Snapshot,
  endSnapshot: Snapshot,
  transitionMode: TransitionMode,
  transitionPaths?: TransitionPaths | null,
): number => {
  switch (transitionMode) {
    case TransitionMode.TIME_BASED:
      return (TRANSITION_TIME / 1000) * FPS;

    case TransitionMode.DISTANCE_BASED:
      const maxDistance = getMaxDistanceInTransition(
        startSnapshot,
        endSnapshot,
        transitionPaths,
      );
      const transitionTimeSeconds = maxDistance / TRANSITION_SPEED;
      return Math.max(1, Math.round(transitionTimeSeconds * FPS));

    default:
      return (TRANSITION_TIME / 1000) * FPS;
  }
};

export const getScaledDiffVector = (
  u: Position,
  v: Position,
  factor: number,
): Position => {
  return {
    x: ((u.x as number) - (v.x as number)) / factor,
    y: ((u.y as number) - (v.y as number)) / factor,
  };
};

export const getScaledSumVector = (
  u: Position,
  v: Position,
  factor: number,
): Position => {
  return {
    x: (u.x as number) + (v.x as number) * factor,
    y: (u.y as number) + (v.y as number) * factor,
  };
};

// Helper: Interpolate position based on current frame and total frames
export const getInterpolatedPosition = (
  startPos: Position,
  endPos: Position,
  currentFrame: number,
  totalFramesForObject: number,
): Position => {
  // If we've exceeded the object's frame count, it has reached its destination
  if (currentFrame >= totalFramesForObject) {
    return endPos;
  }

  // Calculate interpolation factor (0 to 1)
  const t = currentFrame / totalFramesForObject;

  return {
    x:
      (startPos.x as number) +
      ((endPos.x as number) - (startPos.x as number)) * t,
    y:
      (startPos.y as number) +
      ((endPos.y as number) - (startPos.y as number)) * t,
  };
};

// Calculate per-object frame counts for DISTANCE_BASED mode
export const calculateObjectFrameCounts = (
  startSnapshot: Snapshot,
  endSnapshot: Snapshot,
  transitionPaths?: TransitionPaths | null,
): {
  ball: number;
  goalkeepers: number[];
  players: number[];
  opponents: number[];
} => {
  const ballDistance = getEffectiveDistance(
    startSnapshot.ball,
    endSnapshot.ball,
    transitionPaths?.ball,
  );
  const ballFrameCount = calculateFrameCountForDistance(ballDistance);

  const goalkeeperFrameCounts = startSnapshot.goalkeepers.map((startPos, i) => {
    const endPos = endSnapshot.goalkeepers[i];
    if (startPos && endPos) {
      const distance = getEffectiveDistance(
        startPos as Position,
        endPos as Position,
        transitionPaths?.goalkeepers[i],
      );
      return calculateFrameCountForDistance(distance);
    }
    return 1;
  });

  const playerFrameCounts = startSnapshot.players.map((startPos, i) => {
    const endPos = endSnapshot.players[i];
    if (startPos && endPos) {
      const distance = getEffectiveDistance(
        startPos as Position,
        endPos as Position,
        transitionPaths?.players[i],
      );
      return calculateFrameCountForDistance(distance);
    }
    return 1;
  });

  const opponentFrameCounts = startSnapshot.opponents.map((startPos, i) => {
    const endPos = endSnapshot.opponents[i];
    if (startPos && endPos) {
      const distance = getEffectiveDistance(
        startPos as Position,
        endPos as Position,
        transitionPaths?.opponents[i],
      );
      return calculateFrameCountForDistance(distance);
    }
    return 1;
  });

  return {
    ball: ballFrameCount,
    goalkeepers: goalkeeperFrameCounts,
    players: playerFrameCounts,
    opponents: opponentFrameCounts,
  };
};

// Generate the diff snapshot used for TIME_BASED interpolation
export const generateTransitionDiffSnapshot = (
  startSnapshot: Snapshot,
  endSnapshot: Snapshot,
  transitionFrameCount: number,
): Snapshot => {
  return {
    ball: getScaledDiffVector(
      endSnapshot.ball,
      startSnapshot.ball,
      transitionFrameCount,
    ),
    goalkeepers: endSnapshot.goalkeepers.map((endPos, index) =>
      getScaledDiffVector(
        endPos as unknown as Position,
        startSnapshot.goalkeepers[index] as unknown as Position,
        transitionFrameCount,
      ),
    ),
    players: endSnapshot.players.map((endPos, index) =>
      getScaledDiffVector(
        endPos as unknown as Position,
        startSnapshot.players[index] as unknown as Position,
        transitionFrameCount,
      ),
    ),
    opponents: endSnapshot.opponents.map((endPos, index) =>
      getScaledDiffVector(
        endPos as unknown as Position,
        startSnapshot.opponents[index] as unknown as Position,
        transitionFrameCount,
      ),
    ),
  };
};

// Position along a prepared path at the given frame, arc-length
// parameterized so the piece moves at constant speed along its curve.
const getPathPosition = (
  prepared: PreparedPath,
  currentFrame: number,
  totalFramesForObject: number,
): Position => {
  const t =
    totalFramesForObject <= 0
      ? 1
      : Math.min(1, currentFrame / totalFramesForObject);
  return getPositionAlongPath(prepared.points, t, prepared.cumulative);
};

// Compute the interpolated snapshot for the current transition frame.
// Pieces with a prepared path follow it (both modes); the rest keep the
// original linear behaviour. totalTransitionFrames drives path progress in
// TIME_BASED mode, where the legacy diff-vector math has no notion of t.
export const computeInterpolatedSnapshot = (
  originSnapshot: Snapshot,
  startSnapshot: Snapshot,
  endSnapshot: Snapshot,
  diffSnapshot: Snapshot,
  currentFrame: number,
  transitionMode: TransitionMode,
  objectFrameCounts: {
    ball: number;
    goalkeepers: number[];
    players: number[];
    opponents: number[];
  } | null,
  transitionPaths?: TransitionPaths | null,
  totalTransitionFrames?: number,
): Snapshot => {
  if (
    transitionMode === TransitionMode.DISTANCE_BASED &&
    objectFrameCounts
  ) {
    return {
      ball: transitionPaths?.ball
        ? getPathPosition(
            transitionPaths.ball,
            currentFrame,
            objectFrameCounts.ball,
          )
        : getInterpolatedPosition(
            startSnapshot.ball,
            endSnapshot.ball,
            currentFrame,
            objectFrameCounts.ball,
          ),
      goalkeepers: originSnapshot.goalkeepers.map((_, index) => {
        const prepared = transitionPaths?.goalkeepers[index];
        return prepared
          ? getPathPosition(
              prepared,
              currentFrame,
              objectFrameCounts.goalkeepers[index],
            )
          : getInterpolatedPosition(
              startSnapshot.goalkeepers[index] as Position,
              endSnapshot.goalkeepers[index] as Position,
              currentFrame,
              objectFrameCounts.goalkeepers[index],
            );
      }),
      players: originSnapshot.players.map((_, index) => {
        const prepared = transitionPaths?.players[index];
        return prepared
          ? getPathPosition(
              prepared,
              currentFrame,
              objectFrameCounts.players[index],
            )
          : getInterpolatedPosition(
              startSnapshot.players[index] as Position,
              endSnapshot.players[index] as Position,
              currentFrame,
              objectFrameCounts.players[index],
            );
      }),
      opponents: originSnapshot.opponents.map((_, index) => {
        const prepared = transitionPaths?.opponents[index];
        return prepared
          ? getPathPosition(
              prepared,
              currentFrame,
              objectFrameCounts.opponents[index],
            )
          : getInterpolatedPosition(
              startSnapshot.opponents[index] as Position,
              endSnapshot.opponents[index] as Position,
              currentFrame,
              objectFrameCounts.opponents[index],
            );
      }),
      // Carry the target snapshot's stored paths through: the interpolated
      // snapshot is what a drag started mid-transition writes back, and it
      // must not silently drop the recorded paths.
      paths: endSnapshot.paths ?? null,
    };
  }

  // TIME_BASED mode: use diff vector approach (path pieces sample their
  // polyline by global transition progress instead).
  const totalFrames = totalTransitionFrames || 0;
  return {
    ball: transitionPaths?.ball
      ? getPathPosition(transitionPaths.ball, currentFrame, totalFrames)
      : getScaledSumVector(
          originSnapshot.ball,
          diffSnapshot.ball,
          currentFrame,
        ),
    goalkeepers: originSnapshot.goalkeepers.map((pos, index) => {
      const prepared = transitionPaths?.goalkeepers[index];
      return prepared
        ? getPathPosition(prepared, currentFrame, totalFrames)
        : getScaledSumVector(
            pos as unknown as Position,
            diffSnapshot.goalkeepers[index] as unknown as Position,
            currentFrame,
          );
    }),
    players: originSnapshot.players.map((pos, index) => {
      const prepared = transitionPaths?.players[index];
      return prepared
        ? getPathPosition(prepared, currentFrame, totalFrames)
        : getScaledSumVector(
            pos as unknown as Position,
            diffSnapshot.players[index] as unknown as Position,
            currentFrame,
          );
    }),
    opponents: originSnapshot.opponents.map((pos, index) => {
      const prepared = transitionPaths?.opponents[index];
      return prepared
        ? getPathPosition(prepared, currentFrame, totalFrames)
        : getScaledSumVector(
            pos as unknown as Position,
            diffSnapshot.opponents[index] as unknown as Position,
            currentFrame,
          );
    }),
    paths: endSnapshot.paths ?? null,
  };
};
